import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthRequired } from "@/components/auth/AuthRequired";
import { useUrlState } from "@/lib/useUrlState";
import { Card, Tabs } from "@/components/ui";
import { ContentTopBar, TopBarTitle } from "@/components/layout/ContentTopBar";
import { PortfolioHero } from "@/components/portfolio/PortfolioHero";
import { PositionsTab } from "@/components/portfolio/PositionsTab";
import { OpenOrdersTab } from "@/components/portfolio/OpenOrdersTab";
import { HistoryTab } from "@/components/portfolio/HistoryTab";
import { useTradingModeOrCash } from "@/mode";
import { fetchOrders, fetchPortfolio, keys } from "@/lib/queries";
import { FileChartColumn } from "@/icons";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/_app/portfolio")({
  head: () =>
    buildHead({
      path: "/portfolio",
      title: "Portfolio",
      description: "Your positions, open orders and trade history.",
      noindex: true,
    }),
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: string;
    action?: string;
    bucket?: string;
    orders_filter?: string;
    positions_page?: number;
    orders_page?: number;
    history_page?: number;
  } => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
    action: typeof s.action === "string" ? s.action : undefined,
    bucket: typeof s.bucket === "string" ? s.bucket : undefined,
    orders_filter: typeof s.orders_filter === "string" ? s.orders_filter : undefined,
    positions_page: pageParam(s.positions_page),
    orders_page: pageParam(s.orders_page),
    history_page: pageParam(s.history_page),
  }),
  component: () => (
    <AuthRequired>
      <Portfolio />
    </AuthRequired>
  ),
});

function pageParam(raw: unknown) {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function Portfolio() {
  // The tab components build their query keys from the module-level mode
  // register but do NOT consume the mode context, and the provider's
  // children-reference bailout means a switch re-renders only consumers —
  // so the list under the (subscribed, instantly-updating) hero kept its
  // old key until something else re-rendered it. Subscribing HERE and
  // keying the container remounts the active tab per mode, which also
  // resets pagination and search: CASH's page 3 is not a page of GAME's
  // dataset.
  const tradingMode = useTradingModeOrCash();
  const { t } = useTranslation("portfolio");
  const [tab, setTab] = useUrlState("tab", "positions");
  // Append the record total in parens once it's loaded ("Open Order (18)");
  // the bare label shows until the count probe resolves so we never flash "(0)".
  const withCount = (label: string, count?: number) =>
    count == null ? label : t("tabs.withCount", { label, count });
  // Tab counts: a 1-row probe of each list, read only for page.total. The
  // server returns the same active sets the tabs render (portfolio excludes
  // archived statuses, orders default to the active set) so these totals match
  // the visible rows. Keyed under portfolio()/orders() so the existing
  // place/cancel/redeem invalidations refresh them; orders poll like their tab.
  const positionCount = useQuery({
    queryKey: keys.portfolioPage(1, 0),
    queryFn: () => fetchPortfolio({ limit: 1, offset: 0 }),
  }).data?.page.total;
  const orderCount = useQuery({
    queryKey: keys.ordersPage(1, 0),
    queryFn: () => fetchOrders({ limit: 1, offset: 0 }),
    refetchInterval: 5_000,
  }).data?.page.total;
  return (
    <div className="mx-auto w-full max-w-[834px] py-2 sm:px-6 lg:max-w-none lg:px-0 lg:pt-0">
      {/* top bar spans the full content width (title left, bell + balance pinned
          top-right) — the -mx bleed reaches main's edges since the root is
          full-width on lg. The pull-up is desktop-only (lg:-mt-4) so mobile keeps
          main's top padding under the Header instead of jamming the title against
          it (matches Home/Explore/Profile); `divider` draws the full-bleed
          hairline below the bar. */}
      <ContentTopBar className="-mx-4 mb-5 px-4 lg:-mx-6 lg:-mt-4 lg:px-6" divider>
        <TopBarTitle icon={<FileChartColumn className="h-6 w-6 shrink-0" />}>{t("title")}</TopBarTitle>
      </ContentTopBar>
      {/* body capped + centred on desktop so the hero/table don't stretch
          across the full-width content area (lg-only; mobile keeps its layout). */}
      <div className="lg:mx-auto lg:max-w-6xl">
        <div className="sm:mx-4 lg:mx-0">
          <PortfolioHero />
        </div>
        <Card className="min-h-[22rem] overflow-hidden bg-surface/30 lg:min-h-[26rem]">
          <Tabs
            fullWidth
            value={tab}
            onValueChange={setTab}
            items={[
              { value: "positions", label: withCount(t("tabs.position"), positionCount) },
              { value: "orders", label: withCount(t("tabs.openOrder"), orderCount) },
              { value: "history", label: t("tabs.history") },
            ]}
          />
          <div key={tradingMode} className="px-3 pt-5 pb-8">
            {tab === "orders" ? <OpenOrdersTab /> : tab === "history" ? <HistoryTab /> : <PositionsTab />}
          </div>
        </Card>
      </div>
    </div>
  );
}
